import { Routes as RouterRoutes } from 'react-router-dom';

// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  ReferralProgramConfigurationController,
  ReferralProgramDetailsController,
  routeCreator as referralProgramRouteCreator,
} from '@occc/feat-referral-program';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { routeCreator as promocodesRouterCreator, Promocodes } from '@occc/feat-promocodes';
import { segmentsFactory } from '@occc/feat-segments';
import { touchpointRoutes } from '@occc/feat-touchpoint';
import { useConfig } from '@occc/config';
import { ConfigGuard, ChainGuard } from '@occc-app/App/guards';
import Dashboard from '@occc-app/Dashboard';
import { SentryRoute as Route } from '@occc-app/App/sentry';
import { Feature } from '@occc/utils-access-control';
import {
  MemberDetailsPage,
  MemberListPage,
  MemberPointChangePage,
  MemberTransactionPage,
} from '@occc-app/routes/Member';
import { routeCreator as memberRouteCreator } from '@occc-app/routes/Member/utils';
import {
  AudienceCreatePage,
  AudienceDetailsPage,
  AudienceExportPage,
  AudienceListPage,
  AudienceUpdatePage,
} from '@occc-app/routes/Audience';
import { routeCreator as audiencesRouteCreator } from '@occc-app/routes/Audience/utils';
import { OffersComponent } from '@occc-app/Offers';
import OfferDetailsPage from '@occc-app/Offer/OfferDetailsPage';
import {
  ProductGroupCreatePage,
  ProductGroupDetailsPage,
  ProductGroupListPage,
  ProductGroupUpdatePage,
} from '@occc-app/routes/ProductGroup';
import { routeCreator as productGroupsRouteCreator } from '@occc-app/routes/ProductGroup/utils';
import {
  CreateWalletPage,
  EditWalletPage,
  WalletDetailsPage,
  WalletListPage,
} from '@occc-app/routes/Wallet';
import {
  CreateSurveyPage,
  SurveyDetailsPage,
  SurveyListPage,
  UpdateSurveyPage,
} from '@occc-app/routes/Survey';
import { routeCreator as surveyRouteCreator } from '@occc-app/routes/Survey/routeCreator';
import { routeCreator as walletRouteCreator } from '@occc-app/routes/Wallet/utils';
import {
  AwardCatalogCreatePage,
  AwardCatalogDetailsPage,
  AwardCatalogListPage,
  AwardCatalogUpdatePage,
} from '@occc-app/routes/AwardCatalog';
import { routeCreator as awardRouteCreator } from '@occc-app/routes/AwardCatalog/utils';
import {
  SweepstakesConfigurePage,
  SweepstakesCreatePage,
  SweepstakesUpdatePage,
} from '@occc-app/routes/Sweepstakes';
import { routeCreator as sweepstakesRouteCreator } from '@occc-app/routes/Sweepstakes/utils';
import {
  BillboardConfigurePage,
  BillboardCreatePage,
  BillboardUpdatePage,
} from '@occc-app/routes/Billboard';
import { routeCreator as billboardRouteCreator } from '@occc-app/routes/Billboard/utils';
import {
  CampaignCreatePage,
  CampaignDetailsPage,
  CampaignListPage,
  CampaignUpdatePage,
} from '@occc-app/routes/Campaign';
import { routeCreator as campaignRouteCreator } from '@occc-app/routes/Campaign/utils';
import CampaignComponentOffer from '@occc-app/routes/Offer/Offer';
import { routeCreator as offferRouteCreator } from '@occc-app/routes/Offer/utils';
import CampaignComponentPromotion from '@occc-app/routes/Promotion/Promotion';
import { routeCreator as promotionRouteCreator } from '@occc-app/routes/Promotion/utils';
import CampaignComponentReward from '@occc-app/routes/Reward/Reward';
import { routeCreator as rewardRouteCreator } from '@occc-app/routes/Reward/utils';
import CampaignComponentPunchcard from '@occc-app/routes/Punchcard/Punchcard';
import CampaignComponentChallenge from '@occc-app/routes/Challenge/Challenge';
import { routeCreator as punchcardRouteCreator } from '@occc-app/routes/Punchcard/utils';
import { routeCreator as challengeRouteCreator } from '@occc-app/routes/Challenge/utils';
import { ComponentDetailsPageContainer as ComponentDetailsPageV2 } from '@occc-app/routes/CampaignComponents/component-details';
import { ProgramManagementPage } from '@occc-app/routes/ProgramManagement';
import { routeCreator as programManagementRouteCreator } from '@occc-app/routes/ProgramManagement/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  routeCreator as triggeredMessagesRouteCreator,
  TriggeredMessagesRoutes,
} from '@occc/feat-triggered-messages';
import { CreateUserPage, UpdateUserPage, UserListPage } from '@occc-app/routes/User';
import { routeCreator as usersRouteCreator } from '@occc-app/routes/User/utils';
import TransactionsListPage from '@occc-app/routes/Transactions/TransactionsListPage';
import TransactionDetailsPage from '@occc-app/routes/Transactions/transaction-details';
import { routeCreator as transactionsRouteCreator } from '@occc-app/routes/Transactions/utils';
import RedirectByUserRole from '@occc-app/routes/RedirectByUserRole';
import { OfferDetailsRedirectPage, OfferEditRedirectPage } from '@occc-app/routes/OfferRedirect';

// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  routeCreator as messagingCenterRouteCreator,
  MessagingCenterPage,
  MessagingCampaignsListPage,
  MessagingCampaignDetailsPageContainer,
  MessagingCampaignCreatePageContainer,
  MessagingCampaignUpdatePageContainer,
  MessagingCampaignConfigureContainer,
} from '@occc/feat-messaging-center';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  routeCreator as scanDataRouteCreator,
  ScanDataPage,
  ScanDataSubmissionGroupsCreate,
  ScanDataSubmissionGroupsUpdate,
  ScanDataSubmissionGroupsDetailsPage,
  ScanDataPastSubmissionPage,
} from '@occc/feat-scan-data';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  TobaccoApiOffersPage,
  TobaccoApiOfferDetailsPage,
  routeCreator as tobaccoApiOffersRouteCreator,
} from '@occc/feat-tobacco-api-offers';
import { driveInsightsRoutes, DriveInsightsLayout } from '@occc/feat-drive';
import {
  LocationCreatePage,
  LocationListPage,
  LocationUpdatePage,
} from '@occc-app/routes/Location';
import { routeCreator as locationRouteCreator } from '@occc-app/routes/Location/utils';
import {
  CreateDisplayCategoryPage,
  DisplayCategoryDetailsPage,
  DisplayCategoryListPage,
  UpdateDisplayCategoryPage,
} from '@occc-app/routes/DisplayCategory';
import { routeCreator as displayCategoryRouteCreator } from '@occc-app/routes/DisplayCategory/utils';
import {
  CreateProductCategoryPage,
  ProductCategoryDetailsPage,
  ProductCategoryListPage,
  UpdateProductCategoryPage,
} from '@occc-app/routes/ProductCategory';
import { routeCreator as productCategoryRouteCreator } from '@occc-app/routes/ProductCategory/utils';
import LoyaltyEventListPage from '@occc-app/routes/LoyaltyEvent/LoyaltyEventListPage';
import LoyaltyEventCreatePage from '@occc-app/routes/LoyaltyEvent/LoyaltyEventCreatePage';
import LoyaltyEventDetailsPage from '@occc-app/routes/LoyaltyEvent/LoyaltyEventDetailsPage';
import LoyaltyEventUpdatePage from '@occc-app/routes/LoyaltyEvent/LoyaltyEventUpdatePage';
import LoyaltyEventConfigurePage from '@occc-app/routes/LoyaltyEvent/LoyaltyEventConfigurePage';
import { routeCreator as loyaltyEventRouteCreator } from '@occc-app/routes/LoyaltyEvent/utils';
import { LocationGroupDetailsPage } from '@occc-app/routes/Location/LocationGroup/LocationGroupDetailsPage';

import { AudienceSectionRefsProvider } from '@occc-app/routes/Audience/context/AudienceSectionRefsContext';
import { AudienceExpandedSectionsProvider } from '@occc-app/routes/Audience/context/AudienceExpandedSectionsContext';

import {
  CampaignManagementGameConfigurePageContainer,
  CampaignManagementGameCreatePageContainer,
  CampaignManagementGameUpdatePageContainer,
  ProgramManagementGameConfigurePageContainer,
  ProgramManagementGameCreatePageContainer,
  ProgramManagementGameDetailsPageContainer,
  ProgramManagementGameListPageContainer,
  ProgramManagementGameUpdatePageContainer,
} from '@occc-app/routes/Game';
import {
  campaignManagementGameRouteCreator,
  programManagementGameRouteCreator,
} from '@occc-app/routes/Game/utils';

import { AppLayout, SidebarAppShell } from '@occc-app/design-system';
import { retailerConnectRoutes } from '@occc/feat-retailer-connect';
import { storedValueRoutes } from '@occc/feat-stored-value';

import { ProtectedRoute } from './ProtectedRoute';
import { NotFound } from './NotFound';

export const AuthenticatedRoutes = () => {
  const { version: segmentsVersion } = useConfig('features.segments');
  const { routes: segmentsRoutes } = segmentsFactory(segmentsVersion);

  return (
    <RouterRoutes>
      <Route element={<SidebarAppShell />}>
        {/* Drive Insights: sidebar + white background using DriveInsightsLayout (with app bar and hero) */}
        <Route element={<DriveInsightsLayout />}>{driveInsightsRoutes}</Route>

        {/* All other routes get the full AppLayout (hero, app bar, footer) */}
        <Route element={<AppLayout />}>
          <Route path='/' element={<RedirectByUserRole />} />
          {/* TODO: It looks like the rudimental route, the "/" route should redirect the user to the needed page */}
          <Route path='/redirect-by-role' element={<RedirectByUserRole />} />
          <Route path='/edit/:offerId/*' element={<OfferEditRedirectPage />} />
          <Route path='/details/:offerId' element={<OfferDetailsRedirectPage />} />
          <Route
            path='/dashboard'
            element={
              <ProtectedRoute feature={Feature.DASHBOARD_VIEW}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={memberRouteCreator.base()}
            element={
              <ProtectedRoute feature={Feature.MEMBERS_VIEW}>
                <MemberListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={memberRouteCreator.details(':id')}
            element={
              <ProtectedRoute feature={Feature.MEMBER_VIEW}>
                <MemberDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${memberRouteCreator.transaction(':id')}/*`}
            element={
              <ProtectedRoute feature={Feature.MEMBER_VIEW}>
                <MemberTransactionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${memberRouteCreator.pointChange(':id')}/*`}
            element={
              <ProtectedRoute feature={Feature.MEMBER_VIEW}>
                <MemberPointChangePage />
              </ProtectedRoute>
            }
          />
          <Route
            path='/offers'
            element={
              <ProtectedRoute feature={Feature.OFFERS_VIEW}>
                <OffersComponent />
              </ProtectedRoute>
            }
          />
          <Route
            path='/offers/:id'
            element={
              <ProtectedRoute feature={Feature.OFFER_VIEW}>
                <OfferDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={productGroupsRouteCreator.list()}
            element={
              <ProtectedRoute feature={Feature.PRODUCT_GROUPS_VIEW}>
                <ProductGroupListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={productGroupsRouteCreator.create()}
            element={
              <ProtectedRoute feature={Feature.PRODUCT_GROUP_CREATE}>
                <ProductGroupCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={productGroupsRouteCreator.details(':id')}
            element={
              <ProtectedRoute feature={Feature.PRODUCT_GROUP_VIEW}>
                <ProductGroupDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={productGroupsRouteCreator.update(':id')}
            element={
              <ProtectedRoute feature={Feature.PRODUCT_GROUP_EDIT}>
                <ProductGroupUpdatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={walletRouteCreator.create()}
            element={
              <ConfigGuard isFeatureEnabled={(config) => !!config.features?.walletSteeringEnabled}>
                <ProtectedRoute feature={Feature.WALLET_CREATE}>
                  <CreateWalletPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={walletRouteCreator.update(':id')}
            element={
              <ConfigGuard isFeatureEnabled={(config) => !!config.features?.walletSteeringEnabled}>
                <ProtectedRoute feature={[Feature.WALLET_EDIT, Feature.WALLET_EDIT_VIEW_ONLY]}>
                  <EditWalletPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={walletRouteCreator.details(':id')}
            element={
              <ConfigGuard isFeatureEnabled={(config) => !!config.features?.walletSteeringEnabled}>
                <ProtectedRoute feature={Feature.WALLET_VIEW}>
                  <WalletDetailsPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={walletRouteCreator.list()}
            element={
              <ConfigGuard isFeatureEnabled={(config) => !!config.features?.walletSteeringEnabled}>
                <ProtectedRoute feature={Feature.WALLETS_VIEW}>
                  <WalletListPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={`${surveyRouteCreator.create()}/*`}
            element={
              <ProtectedRoute feature={Feature.SURVEY_CREATE}>
                <CreateSurveyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={tobaccoApiOffersRouteCreator.details(':id')}
            element={
              <ConfigGuard isFeatureEnabled={(config) => config.externalTobaccoApiOffer.enabled}>
                <ProtectedRoute feature={Feature.TOBACCO_API_OFFERS_VIEW}>
                  <TobaccoApiOfferDetailsPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={`${tobaccoApiOffersRouteCreator.base()}/*`}
            element={
              <ConfigGuard isFeatureEnabled={(config) => config.externalTobaccoApiOffer.enabled}>
                <ProtectedRoute feature={Feature.TOBACCO_API_OFFERS_VIEW}>
                  <TobaccoApiOffersPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={`${surveyRouteCreator.update(':id')}/*`}
            element={
              <ProtectedRoute feature={[Feature.SURVEY_EDIT, Feature.SURVEY_EDIT_VIEW_ONLY]}>
                <UpdateSurveyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={surveyRouteCreator.details(':id')}
            element={
              <ProtectedRoute feature={Feature.SURVEY_VIEW}>
                <SurveyDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={surveyRouteCreator.list()}
            element={
              <ProtectedRoute feature={Feature.SURVEYS_VIEW}>
                <SurveyListPage />
              </ProtectedRoute>
            }
          />
          <Route
            key='ReferralProgramDetailsController'
            path={referralProgramRouteCreator.details()}
            element={
              <ChainGuard
                isFeatureEnabled={(chainConfig) => chainConfig.referralProgramConfigurationEnabled}
              >
                <ProtectedRoute feature={Feature.REFERRAL_PROGRAM_VIEW}>
                  <ReferralProgramDetailsController />
                </ProtectedRoute>
              </ChainGuard>
            }
          />
          <Route
            key='ReferralProgramConfigurationController'
            path={referralProgramRouteCreator.configure()}
            element={
              <ChainGuard
                isFeatureEnabled={(chainConfig) => chainConfig.referralProgramConfigurationEnabled}
              >
                <ProtectedRoute
                  feature={[Feature.REFERRAL_PROGRAM_EDIT, Feature.REFERRAL_PROGRAM_EDIT_VIEW_ONLY]}
                >
                  <ReferralProgramConfigurationController />
                </ProtectedRoute>
              </ChainGuard>
            }
          />
          {segmentsRoutes.map(({ path, feature, component: Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute feature={feature}>
                  <Component />
                </ProtectedRoute>
              }
            />
          ))}
          {touchpointRoutes}
          {storedValueRoutes}
          <Route
            path={audiencesRouteCreator.base()}
            element={
              <ProtectedRoute feature={Feature.AUDIENCES_VIEW}>
                <AudienceListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={audiencesRouteCreator.create()}
            element={
              <ProtectedRoute feature={Feature.AUDIENCE_CREATE}>
                <AudienceSectionRefsProvider>
                  <AudienceExpandedSectionsProvider>
                    <AudienceCreatePage />
                  </AudienceExpandedSectionsProvider>
                </AudienceSectionRefsProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path={audiencesRouteCreator.update(':id')}
            element={
              <ProtectedRoute feature={[Feature.AUDIENCE_EDIT, Feature.AUDIENCE_EDIT_VIEW_ONLY]}>
                <AudienceSectionRefsProvider>
                  <AudienceExpandedSectionsProvider>
                    <AudienceUpdatePage />
                  </AudienceExpandedSectionsProvider>
                </AudienceSectionRefsProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path={audiencesRouteCreator.details(':id')}
            element={
              <ProtectedRoute feature={Feature.AUDIENCE_VIEW}>
                <AudienceDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={audiencesRouteCreator.export(':id', ':reportId')}
            element={
              <ProtectedRoute feature={Feature.AUDIENCE_EXPORT}>
                <AudienceExportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={campaignRouteCreator.list()}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGNS_VIEW}>
                <CampaignListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={campaignRouteCreator.create()}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_CREATE}>
                <CampaignCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={campaignRouteCreator.update(':id')}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_EDIT}>
                <CampaignUpdatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={campaignRouteCreator.details(':id')}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_VIEW}>
                <CampaignDetailsPage />
              </ProtectedRoute>
            }
          />
          {/* This is deprecated and will be removed soon, along with all linked items OCST-22238 */}
          {/*<Route*/}
          {/*  path='/campaigns/:id/:componentId'*/}
          {/*  element={*/}
          {/*    <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_VIEW}>*/}
          {/*      <ComponentDetailsPage />*/}
          {/*    </ProtectedRoute>*/}
          {/*  }*/}
          {/*/>*/}
          <Route
            path='/campaigns/:id/:componentId'
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_VIEW}>
                <ComponentDetailsPageV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${offferRouteCreator.base(':id')}/*`}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_VIEW}>
                <CampaignComponentOffer />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${promotionRouteCreator.base(':id')}/*`}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_VIEW}>
                <CampaignComponentPromotion />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${promocodesRouterCreator.base(':id')}/*`}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_VIEW}>
                <Promocodes />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${rewardRouteCreator.base(':id')}/*`}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_VIEW}>
                <CampaignComponentReward />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${punchcardRouteCreator.base(':id')}/*`}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_VIEW}>
                <CampaignComponentPunchcard />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${challengeRouteCreator.base(':id')}/*`}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_VIEW}>
                <CampaignComponentChallenge />
              </ProtectedRoute>
            }
          />
          <Route
            path={usersRouteCreator.list()}
            element={
              <ProtectedRoute feature={Feature.USER_MANAGEMENT_VIEW}>
                <UserListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={usersRouteCreator.update(':userUuid')}
            element={
              <ProtectedRoute feature={Feature.USER_MANAGEMENT_EDIT}>
                <UpdateUserPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={usersRouteCreator.create()}
            element={
              <ProtectedRoute feature={Feature.USER_MANAGEMENT_CREATE}>
                <CreateUserPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={awardRouteCreator.base()}
            element={
              <ProtectedRoute feature={Feature.AWARDS_VIEW}>
                <AwardCatalogListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={awardRouteCreator.create()}
            element={
              <ProtectedRoute feature={Feature.AWARD_CREATE}>
                <AwardCatalogCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={awardRouteCreator.details(':id')}
            element={
              <ProtectedRoute feature={Feature.AWARD_VIEW}>
                <AwardCatalogDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={awardRouteCreator.update(':id')}
            element={
              <ProtectedRoute feature={[Feature.AWARD_EDIT, Feature.AWARD_EDIT_VIEW_ONLY]}>
                <AwardCatalogUpdatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={sweepstakesRouteCreator.create(':id')}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_CREATE}>
                <SweepstakesCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${sweepstakesRouteCreator.configure(':id', ':sweepstakesId')}/*`}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_CREATE}>
                <SweepstakesConfigurePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${sweepstakesRouteCreator.update(':id', ':sweepstakesId')}/*`}
            element={
              <ProtectedRoute
                feature={[
                  Feature.CAMPAIGN_COMPONENT_EDIT,
                  Feature.CAMPAIGN_COMPONENT_EDIT_VIEW_ONLY,
                ]}
              >
                <SweepstakesUpdatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={billboardRouteCreator.create(':id')}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_CREATE}>
                <BillboardCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${billboardRouteCreator.configure(':id', ':billboardId')}/*`}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_CREATE}>
                <BillboardConfigurePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${billboardRouteCreator.update(':id', ':billboardId')}/*`}
            element={
              <ProtectedRoute
                feature={[
                  Feature.CAMPAIGN_COMPONENT_EDIT,
                  Feature.CAMPAIGN_COMPONENT_EDIT_VIEW_ONLY,
                ]}
              >
                <BillboardUpdatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={transactionsRouteCreator.base()}
            element={
              <ProtectedRoute feature={Feature.TRANSACTIONS_VIEW}>
                <TransactionsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={transactionsRouteCreator.details(':transactionId')}
            element={
              <ProtectedRoute feature={Feature.TRANSACTION_VIEW}>
                <TransactionDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={programManagementRouteCreator.base()}
            element={
              <ProtectedRoute feature={Feature.PROGRAM_MANAGEMENT_VIEW}>
                <ProgramManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={triggeredMessagesRouteCreator.base.path}
            element={
              <ConfigGuard isFeatureEnabled={(config) => !!config.triggeredMessages.enabled}>
                <TriggeredMessagesRoutes />
              </ConfigGuard>
            }
          />
          <Route
            path={messagingCenterRouteCreator.base()}
            element={
              <ConfigGuard
                isFeatureEnabled={(config) =>
                  !!config.features?.messagingCenterEnabled || !!config.triggeredMessages?.enabled
                }
              >
                <ProtectedRoute
                  feature={[Feature.MESSAGING_CENTER_VIEW, Feature.TRIGGERED_MESSAGES_VIEW]}
                >
                  <MessagingCenterPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={messagingCenterRouteCreator.campaignsList()}
            element={
              <ConfigGuard isFeatureEnabled={(config) => !!config.features?.messagingCenterEnabled}>
                <ProtectedRoute feature={Feature.MESSAGING_CENTER_VIEW}>
                  <MessagingCampaignsListPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={messagingCenterRouteCreator.campaignCreate()}
            element={
              <ConfigGuard isFeatureEnabled={(config) => !!config.features?.messagingCenterEnabled}>
                <ProtectedRoute feature={Feature.MESSAGING_CENTER_CREATE}>
                  <MessagingCampaignCreatePageContainer />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={`${messagingCenterRouteCreator.campaignConfigure(':id')}/*`}
            element={
              <ConfigGuard isFeatureEnabled={(config) => !!config.features?.messagingCenterEnabled}>
                <ProtectedRoute feature={Feature.MESSAGING_CENTER_CREATE}>
                  <MessagingCampaignConfigureContainer />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={messagingCenterRouteCreator.campaignDetails(':id')}
            element={
              <ConfigGuard isFeatureEnabled={(config) => !!config.features?.messagingCenterEnabled}>
                <ProtectedRoute feature={Feature.MESSAGING_CENTER_VIEW}>
                  <MessagingCampaignDetailsPageContainer />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={`${messagingCenterRouteCreator.campaignUpdate(':id')}/*`}
            element={
              <ConfigGuard isFeatureEnabled={(config) => !!config.features?.messagingCenterEnabled}>
                <ProtectedRoute feature={Feature.MESSAGING_CENTER_EDIT}>
                  <MessagingCampaignUpdatePageContainer />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={`${scanDataRouteCreator.base()}/*`}
            element={
              <ConfigGuard isFeatureEnabled={(config) => config.scanData.enabled}>
                <ProtectedRoute feature={Feature.SCAN_DATA_VIEW}>
                  <ScanDataPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={scanDataRouteCreator.submissionGroupsDetails(':id')}
            element={
              <ConfigGuard isFeatureEnabled={(config) => config.scanData.enabled}>
                <ProtectedRoute feature={Feature.SCAN_DATA_VIEW}>
                  <ScanDataSubmissionGroupsDetailsPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={scanDataRouteCreator.submissionGroupsUpdate(':id')}
            element={
              <ConfigGuard isFeatureEnabled={(config) => config.scanData.enabled}>
                <ProtectedRoute feature={Feature.SCAN_DATA_SUBMISSION_GROUPS_EDIT}>
                  <ScanDataSubmissionGroupsUpdate />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={scanDataRouteCreator.submissionGroupsCreate()}
            element={
              <ConfigGuard isFeatureEnabled={(config) => config.scanData.enabled}>
                <ProtectedRoute feature={Feature.SCAN_DATA_SUBMISSION_GROUPS_CREATE}>
                  <ScanDataSubmissionGroupsCreate />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={scanDataRouteCreator.pastSubmissionView({ date: ':date', type: ':type' })}
            element={
              <ConfigGuard isFeatureEnabled={(config) => config.scanData.enabled}>
                <ProtectedRoute feature={Feature.SCAN_DATA_VIEW}>
                  <ScanDataPastSubmissionPage />
                </ProtectedRoute>
              </ConfigGuard>
            }
          />
          <Route
            path={locationRouteCreator.create()}
            element={
              <ProtectedRoute feature={Feature.LOCATION_CREATE}>
                <LocationCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${locationRouteCreator.generalDetails(':locationId')}/*`}
            element={
              <ProtectedRoute feature={[Feature.LOCATION_EDIT, Feature.LOCATION_EDIT_VIEW_ONLY]}>
                <LocationUpdatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={locationRouteCreator.list()}
            element={
              <ProtectedRoute feature={Feature.LOCATIONS_VIEW}>
                <LocationListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={locationRouteCreator.locationGroupDetails(':id')}
            element={
              <ProtectedRoute
                feature={[Feature.LOCATION_GROUP_EDIT, Feature.LOCATION_GROUP_EDIT_VIEW_ONLY]}
              >
                <LocationGroupDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={displayCategoryRouteCreator.create()}
            element={
              <ProtectedRoute feature={Feature.DISPLAY_CATEGORY_CREATE}>
                <CreateDisplayCategoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={displayCategoryRouteCreator.details(':id')}
            element={
              <ProtectedRoute feature={Feature.DISPLAY_CATEGORY_VIEW}>
                <DisplayCategoryDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={displayCategoryRouteCreator.update(':id')}
            element={
              <ProtectedRoute feature={Feature.DISPLAY_CATEGORY_EDIT}>
                <UpdateDisplayCategoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={displayCategoryRouteCreator.list()}
            element={
              <ProtectedRoute feature={Feature.DISPLAY_CATEGORIES_VIEW}>
                <DisplayCategoryListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={productCategoryRouteCreator.create()}
            element={
              <ProtectedRoute feature={Feature.PRODUCT_CATEGORY_CREATE}>
                <CreateProductCategoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={productCategoryRouteCreator.details(':id')}
            element={
              <ProtectedRoute feature={Feature.PRODUCT_CATEGORY_VIEW}>
                <ProductCategoryDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={productCategoryRouteCreator.update(':id')}
            element={
              <ProtectedRoute feature={Feature.PRODUCT_CATEGORY_EDIT}>
                <UpdateProductCategoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={productCategoryRouteCreator.list()}
            element={
              <ProtectedRoute feature={Feature.PRODUCT_CATEGORIES_VIEW}>
                <ProductCategoryListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={loyaltyEventRouteCreator.base()}
            element={
              <ProtectedRoute feature={Feature.LOYALTY_EVENTS_VIEW}>
                <LoyaltyEventListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={loyaltyEventRouteCreator.create()}
            element={
              <ProtectedRoute feature={Feature.LOYALTY_EVENT_CREATE}>
                <LoyaltyEventCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${loyaltyEventRouteCreator.configure(':offerId')}/*`}
            element={
              <ProtectedRoute feature={Feature.LOYALTY_EVENT_CREATE}>
                <LoyaltyEventConfigurePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${loyaltyEventRouteCreator.update(':offerId')}/*`}
            element={
              <ProtectedRoute
                feature={[Feature.LOYALTY_EVENT_EDIT, Feature.LOYALTY_EVENT_EDIT_VIEW_ONLY]}
              >
                <LoyaltyEventUpdatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={loyaltyEventRouteCreator.details(':id')}
            element={
              <ProtectedRoute feature={Feature.LOYALTY_EVENT_VIEW}>
                <LoyaltyEventDetailsPage />
              </ProtectedRoute>
            }
          />
          {/*region Game routes (Campaign management)*/}
          <Route
            path={campaignManagementGameRouteCreator.create(':id')}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_CREATE}>
                <CampaignManagementGameCreatePageContainer />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${campaignManagementGameRouteCreator.configure(':id', ':gameId')}/*`}
            element={
              <ProtectedRoute feature={Feature.CAMPAIGN_COMPONENT_CREATE}>
                <CampaignManagementGameConfigurePageContainer />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${campaignManagementGameRouteCreator.update(':id', ':gameId')}/*`}
            element={
              <ProtectedRoute
                feature={[
                  Feature.CAMPAIGN_COMPONENT_EDIT,
                  Feature.CAMPAIGN_COMPONENT_EDIT_VIEW_ONLY,
                ]}
              >
                <CampaignManagementGameUpdatePageContainer />
              </ProtectedRoute>
            }
          />
          {/*endregion*/}
          {/*region Game routes (Program management)*/}
          <Route
            path={programManagementGameRouteCreator.base()}
            element={
              <ProtectedRoute feature={Feature.GAMES_VIEW}>
                <ProgramManagementGameListPageContainer />
              </ProtectedRoute>
            }
          />
          <Route
            path={programManagementGameRouteCreator.create()}
            element={
              <ProtectedRoute feature={Feature.GAME_CREATE}>
                <ProgramManagementGameCreatePageContainer />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${programManagementGameRouteCreator.configure(':offerId')}/*`}
            element={
              <ProtectedRoute feature={Feature.GAME_CREATE}>
                <ProgramManagementGameConfigurePageContainer />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${programManagementGameRouteCreator.update(':offerId')}/*`}
            element={
              <ProtectedRoute feature={[Feature.GAME_EDIT, Feature.GAME_EDIT_VIEW_ONLY]}>
                <ProgramManagementGameUpdatePageContainer />
              </ProtectedRoute>
            }
          />
          <Route
            path={programManagementGameRouteCreator.details(':id')}
            element={
              <ProtectedRoute feature={Feature.GAME_VIEW}>
                <ProgramManagementGameDetailsPageContainer />
              </ProtectedRoute>
            }
          />
          {/*endregion*/}

          {retailerConnectRoutes}

          <Route path='/not-found' element={<NotFound />} />
          <Route path='*' element={<NotFound />} />
        </Route>
      </Route>
    </RouterRoutes>
  );
};
